import * as React from "react";
import {getLatestDate, PLAN_TREE_THEME} from "./portfolioUtil";
import {Body, Cell, Header, HeaderCell, HeaderRow, Row, Table} from "@table-library/react-table-library/table";
import {IAccountRollingPerformance} from "../../api/PortfolioApi";
import {ISelectValue} from "../common/Select.component";
import {useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {IApplicationRootState} from "../../../applicationState";
import {hasResearchAccess} from "../../../mainReducerMapSelectors";
import {Link} from "react-router-dom";
import {setPerformanceSelectedProductAction} from "./PortfolioActions";
import {AnyThunkDispatch} from "../../../commonTypes";
import {formatDateAndTimeCustom} from "../../utils/dateUtil";

export interface IPerformanceRollingTableProps {
    selectedDate: ISelectValue;
    accountRollingPerformance: IAccountRollingPerformance[];
}

export interface IPerformanceRollingNodes {
    id: number;
    name: string;
    backstopId: number | null,
    threeYearVsBenchmark: number | null;
    threeYearVsPeerGroup: number | null;
    fiveYearVsBenchmark: number | null;
    fiveYearVsPeerGroup: number | null;
}

export const PerformanceRollingTable: React.FunctionComponent<IPerformanceRollingTableProps> = (props) => {
    const [nodes, setNodes] = React.useState<IPerformanceRollingNodes[]>([]);
    const hasResearch = useSelector((state: IApplicationRootState) => hasResearchAccess(state));
    const dispatch: AnyThunkDispatch = useDispatch();

    useEffect(() => {
        setNodes(() => {
            return props.accountRollingPerformance.filter(item => item.date === (props.selectedDate.id as string))
                .map((item) => ({
                id: item.accountId,
                name: item.accountName,
                backstopId: item.backstopId,
                threeYearVsBenchmark: item.threeYearVsBenchmark,
                threeYearVsPeerGroup: item.threeYearVsPeerGroup,
                fiveYearVsBenchmark: item.fiveYearVsBenchmark,
                fiveYearVsPeerGroup: item.fiveYearVsPeerGroup,
                nodes: []
            }));
        });
    }, [props]);

    const data = {nodes};

    const renderName = (item: IPerformanceRollingNodes) => {
        return hasResearch && item.backstopId
            ? <Link to={`/products/${item.backstopId}`}
                    onClick={() => dispatch(setPerformanceSelectedProductAction(item.id))} id={`link-${item.id}`}
            >
                {item.name}
            </Link>
            : item.name;
    };

    const renderTable = () => {
        return (
            <Table data={data}
                   theme={PLAN_TREE_THEME("34% 16.5% 16.5% 16.5% 16.5%")}
                   layout={{custom: true, fixedHeader: true}}>
                {(nodes: IPerformanceRollingNodes[]) => (
                    <>
                        <Header>
                            <HeaderRow data-testid="performance-rolling-table__header-row-group">
                                <HeaderCell className={"performance-rolling-table__header-first-row-cell"}/>
                                <HeaderCell gridColumnStart={2} gridColumnEnd={4} className={"performance-rolling-table__header performance-rolling-table__header-background"}>
                                    Rolling 3-yr Period
                                </HeaderCell>
                                <HeaderCell gridColumnStart={4} gridColumnEnd={6} className={"performance-rolling-table__header performance-rolling-table__header-background"}>
                                    Rolling 5-yr Period
                                </HeaderCell>
                            </HeaderRow>
                            <HeaderRow data-testid="performance-rolling-table__header-row">
                                <HeaderCell className={"performance-rolling-table__header performance-rolling-table__header-first-cell"}>Category</HeaderCell>
                                <HeaderCell className={"performance-rolling-table__header"}>Performance vs<br /> Benchmark</HeaderCell>
                                <HeaderCell className={"performance-rolling-table__header"}>Performance vs<br /> Peer Group Median</HeaderCell>
                                <HeaderCell className={"performance-rolling-table__header"}>Performance vs<br /> Benchmark</HeaderCell>
                                <HeaderCell className={"performance-rolling-table__header"}>Performance vs<br /> Peer Group Median</HeaderCell>
                            </HeaderRow>
                        </Header>
                        <Body>
                            {nodes.map((item: IPerformanceRollingNodes) => {
                                return (
                                    <Row key={item.id} item={item} className={"performance-rolling-table__body-node"}>
                                        <Cell item={item}>{renderName(item)}</Cell>
                                        <Cell>{item.threeYearVsBenchmark !== null ? `${item.threeYearVsBenchmark}` : "-"}</Cell>
                                        <Cell>{item.threeYearVsPeerGroup !== null ? `${item.threeYearVsPeerGroup}` : "-"}</Cell>
                                        <Cell>{item.fiveYearVsBenchmark !== null ? `${item.fiveYearVsBenchmark}` : "-"}</Cell>
                                        <Cell>{item.fiveYearVsPeerGroup !== null ? `${item.fiveYearVsPeerGroup}` : "-"}</Cell>
                                    </Row>
                                );
                            })}
                        </Body>
                    </>
                )}
            </Table>
        );
    };

    const renderRollingTableText = () => {
        const footerDate = formatDateAndTimeCustom(
            getLatestDate(props.accountRollingPerformance
                .filter(it => it.date === props.selectedDate?.id)
                .map(it => it.lastUpdatedTs)), "MMMM DD, YYYY");
        return <div className="performance-tab__rolling-table-text" data-testid="performance-tab__rolling-table-text">
                Percentages are determined by the outperformance of the trailing 3-Year and 5-Year returns to the
                benchmark and peer group median over the prior twenty quarters. The composition of the peer universe
                can change over time and the percentages displayed on this website were calculated as of {footerDate}.
                As a result, these may differ from those provided in the quarterly investment report, which may be
                based on data pulled at a different date.
        </div>;
    };

    return <div className="performance-rolling-table__container" data-testid="performance-rolling-table__container">
       {renderTable()}
       {renderRollingTableText()}
    </div>;
};